import React, { useState, useEffect } from 'react';
import {
  Card,
  Page,
  Layout,
  Button,
  DataTable,
  Badge,
  Modal,
  TextField,
  FormLayout,
  Toast,
  Frame,
  Loading,
  EmptyState,
  LegacyStack as Stack,
  TextContainer,
  ButtonGroup,
  Banner,
  ProgressBar,
} from '@shopify/polaris';
import { PlusMinor } from '@shopify/polaris-icons';

interface Catalog {
  id: string;
  whatsappCatalogId: string;
  name: string;
  status: 'ACTIVE' | 'INACTIVE' | 'SYNCING';
  autoSync: boolean;
  productCount?: number;
  lastSyncedAt?: string;
  createdAt: string;
}

interface CatalogProduct {
  id: string;
  productId: string;
  whatsappProductId: string;
  retailerId: string;
  product: {
    name: string;
    price: number;
    status: string;
  };
}

interface SyncStatus {
  isRunning: boolean;
  totalProducts: number;
  syncedProducts: number;
  failedProducts: number;
  lastError?: string;
}

interface CatalogManagerProps {
  storeId: string;
  onError?: (error: string) => void;
}

export const CatalogManager: React.FC<CatalogManagerProps> = ({ storeId, onError }) => {
  const [catalogs, setCatalogs] = useState<Catalog[]>([]);
  const [selectedCatalog, setSelectedCatalog] = useState<Catalog | null>(null);
  const [catalogProducts, setCatalogProducts] = useState<CatalogProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);

  // Modal states
  const [createModalActive, setCreateModalActive] = useState(false);
  const [viewProductsModalActive, setViewProductsModalActive] = useState(false);
  const [deleteModalActive, setDeleteModalActive] = useState(false);
  const [catalogToDelete, setCatalogToDelete] = useState<Catalog | null>(null);

  // Form states
  const [newCatalogName, setNewCatalogName] = useState('');
  const [newCatalogAutoSync, setNewCatalogAutoSync] = useState(true);

  // Toast state
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastError, setToastError] = useState(false);

  useEffect(() => {
    loadCatalogs();
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (syncing && selectedCatalog) {
      interval = setInterval(() => {
        checkSyncStatus(selectedCatalog.id);
      }, 2000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [syncing, selectedCatalog]);

  const loadCatalogs = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/catalog', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) throw new Error('Failed to load catalogs');

      const data = await response.json();
      setCatalogs(data.data.catalogs || []);
    } catch (error: any) {
      showToast(error.message || 'Failed to load catalogs', true);
      onError?.(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadCatalogProducts = async (catalogId: string) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/catalog/${catalogId}/products`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) throw new Error('Failed to load products');

      const data = await response.json();
      setCatalogProducts(data.data.products || []);
    } catch (error: any) {
      showToast(error.message || 'Failed to load products', true);
    } finally {
      setLoading(false);
    }
  };

  const createCatalog = async () => {
    if (!newCatalogName.trim()) {
      showToast('Please enter a catalog name', true);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/catalog', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newCatalogName,
          autoSync: newCatalogAutoSync,
        }),
      });

      if (!response.ok) throw new Error('Failed to create catalog');

      const data = await response.json();
      showToast('Catalog created successfully');
      setCreateModalActive(false);
      setNewCatalogName('');
      loadCatalogs();
    } catch (error: any) {
      showToast(error.message || 'Failed to create catalog', true);
    } finally {
      setLoading(false);
    }
  };

  const syncCatalog = async (catalogId: string) => {
    setSyncing(true);
    setSyncStatus({ isRunning: true, totalProducts: 0, syncedProducts: 0, failedProducts: 0 });

    try {
      const response = await fetch(`/api/catalog/${catalogId}/sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) throw new Error('Failed to start sync');

      const data = await response.json();
      showToast(`Syncing ${data.data.totalProducts} products...`);

      // Poll for sync status
      checkSyncStatus(catalogId);
    } catch (error: any) {
      showToast(error.message || 'Failed to sync catalog', true);
      setSyncing(false);
    }
  };

  const checkSyncStatus = async (catalogId: string) => {
    try {
      const response = await fetch(`/api/catalog/${catalogId}/sync/status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) throw new Error('Failed to get sync status');

      const data = await response.json();
      const status: SyncStatus = data.data;

      setSyncStatus(status);

      if (!status.isRunning) {
        setSyncing(false);
        if (status.failedProducts > 0) {
          showToast(
            `Sync completed: ${status.syncedProducts} synced, ${status.failedProducts} failed`,
            false
          );
        } else {
          showToast(`Sync completed: ${status.syncedProducts} products synced`);
        }
        loadCatalogs();
      }
    } catch (error: any) {
      console.error('Failed to check sync status:', error);
    }
  };

  const deleteCatalog = async () => {
    if (!catalogToDelete) return;

    setLoading(true);
    try {
      const response = await fetch(`/api/catalog/${catalogToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) throw new Error('Failed to delete catalog');

      showToast('Catalog deleted successfully');
      setDeleteModalActive(false);
      setCatalogToDelete(null);
      loadCatalogs();
    } catch (error: any) {
      showToast(error.message || 'Failed to delete catalog', true);
    } finally {
      setLoading(false);
    }
  };

  const updateCatalogStatus = async (catalogId: string, status: 'ACTIVE' | 'INACTIVE') => {
    setLoading(true);
    try {
      const response = await fetch(`/api/catalog/${catalogId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status }),
      });

      if (!response.ok) throw new Error('Failed to update catalog');

      showToast(`Catalog ${status === 'ACTIVE' ? 'activated' : 'deactivated'}`);
      loadCatalogs();
    } catch (error: any) {
      showToast(error.message || 'Failed to update catalog', true);
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message: string, isError = false) => {
    setToastMessage(message);
    setToastError(isError);
    setToastActive(true);
  };

  const getBadgeStatus = (status: string): 'success' | 'warning' | 'info' => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'INACTIVE':
        return 'warning';
      case 'SYNCING':
        return 'info';
      default:
        return 'info';
    }
  };

  const catalogRows = catalogs.map((catalog) => [
    catalog.name,
    catalog.whatsappCatalogId,
    <Badge status={getBadgeStatus(catalog.status)}>{catalog.status}</Badge>,
    catalog.productCount || 0,
    catalog.lastSyncedAt
      ? new Date(catalog.lastSyncedAt).toLocaleString()
      : 'Never',
    catalog.autoSync ? 'Yes' : 'No',
    <ButtonGroup>
      <Button
        size="slim"
        onClick={() => {
          setSelectedCatalog(catalog);
          loadCatalogProducts(catalog.id);
          setViewProductsModalActive(true);
        }}
      >
        View Products
      </Button>
      <Button
        size="slim"
        onClick={() => syncCatalog(catalog.id)}
        loading={syncing && selectedCatalog?.id === catalog.id}
        disabled={syncing || catalog.status === 'SYNCING'}
      >
        Sync
      </Button>
      <Button
        size="slim"
        onClick={() => updateCatalogStatus(
          catalog.id,
          catalog.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'
        )}
      >
        {catalog.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
      </Button>
      <Button
        size="slim"
        destructive
        onClick={() => {
          setCatalogToDelete(catalog);
          setDeleteModalActive(true);
        }}
      >
        Delete
      </Button>
    </ButtonGroup>,
  ]);

  const productRows = catalogProducts.map((product) => [
    product.product.name,
    product.retailerId,
    `$${Number(product.product.price).toFixed(2)}`,
    <Badge status={product.product.status === 'ACTIVE' ? 'success' : 'warning'}>
      {product.product.status}
    </Badge>,
  ]);

  const toastMarkup = toastActive ? (
    <Toast
      content={toastMessage}
      error={toastError}
      onDismiss={() => setToastActive(false)}
      duration={3000}
    />
  ) : null;

  return (
    <Frame>
      <Page
        title="WhatsApp Product Catalogs"
        primaryAction={{
          content: 'Create Catalog',
          icon: PlusMinor,
          onAction: () => setCreateModalActive(true),
        }}
      >
        <Layout>
          <Layout.Section>
            {syncing && syncStatus && (
              <Card sectioned>
                <Stack vertical>
                  <TextContainer>
                    <p>
                      <strong>Syncing catalog...</strong>
                    </p>
                    <p>
                      Progress: {syncStatus.syncedProducts} of {syncStatus.totalProducts} products
                    </p>
                    {syncStatus.failedProducts > 0 && (
                      <p style={{ color: '#d72c0d' }}>
                        Failed: {syncStatus.failedProducts}
                      </p>
                    )}
                  </TextContainer>
                  <ProgressBar
                    progress={
                      syncStatus.totalProducts > 0
                        ? (syncStatus.syncedProducts / syncStatus.totalProducts) * 100
                        : 0
                    }
                  />
                </Stack>
              </Card>
            )}

            <Card>
              {loading && catalogs.length === 0 ? (
                <Loading />
              ) : catalogs.length === 0 ? (
                <EmptyState
                  heading="No catalogs yet"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                  action={{
                    content: 'Create Catalog',
                    onAction: () => setCreateModalActive(true),
                  }}
                >
                  <p>Create a WhatsApp catalog to showcase your products in chat.</p>
                </EmptyState>
              ) : (
                <DataTable
                  columnContentTypes={[
                    'text',
                    'text',
                    'text',
                    'numeric',
                    'text',
                    'text',
                    'text',
                  ]}
                  headings={[
                    'Name',
                    'WhatsApp Catalog ID',
                    'Status',
                    'Products',
                    'Last Synced',
                    'Auto Sync',
                    'Actions',
                  ]}
                  rows={catalogRows}
                />
              )}
            </Card>
          </Layout.Section>

          <Layout.Section secondary>
            <Card title="About Catalogs" sectioned>
              <TextContainer>
                <p>
                  WhatsApp catalogs allow you to showcase products directly in WhatsApp
                  conversations.
                </p>
                <p>
                  Enable auto-sync to automatically keep your catalog up to date with your
                  store products.
                </p>
              </TextContainer>
            </Card>

            <Card title="Usage Tips" sectioned>
              <TextContainer>
                <ul>
                  <li>Keep product images high quality (min 500x500px)</li>
                  <li>Use clear, concise product descriptions</li>
                  <li>Sync regularly to keep prices updated</li>
                  <li>Inactive catalogs won't show in messages</li>
                </ul>
              </TextContainer>
            </Card>
          </Layout.Section>
        </Layout>

        {/* Create Catalog Modal */}
        <Modal
          open={createModalActive}
          onClose={() => setCreateModalActive(false)}
          title="Create WhatsApp Catalog"
          primaryAction={{
            content: 'Create',
            onAction: createCatalog,
            loading,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: () => setCreateModalActive(false),
            },
          ]}
        >
          <Modal.Section>
            <FormLayout>
              <TextField
                label="Catalog Name"
                value={newCatalogName}
                onChange={setNewCatalogName}
                placeholder="My Product Catalog"
                autoComplete="off"
              />
              <Banner status="info">
                <p>
                  After creating the catalog, use the "Sync" button to add your store
                  products to WhatsApp.
                </p>
              </Banner>
            </FormLayout>
          </Modal.Section>
        </Modal>

        {/* View Products Modal */}
        <Modal
          large
          open={viewProductsModalActive}
          onClose={() => setViewProductsModalActive(false)}
          title={`Products in ${selectedCatalog?.name}`}
          secondaryActions={[
            {
              content: 'Close',
              onAction: () => setViewProductsModalActive(false),
            },
          ]}
        >
          <Modal.Section>
            {loading ? (
              <Loading />
            ) : catalogProducts.length === 0 ? (
              <EmptyState
                heading="No products in catalog"
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              >
                <p>Use the "Sync" button to add products to this catalog.</p>
              </EmptyState>
            ) : (
              <DataTable
                columnContentTypes={['text', 'text', 'numeric', 'text']}
                headings={['Product Name', 'Retailer ID', 'Price', 'Status']}
                rows={productRows}
              />
            )}
          </Modal.Section>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          open={deleteModalActive}
          onClose={() => setDeleteModalActive(false)}
          title="Delete Catalog"
          primaryAction={{
            content: 'Delete',
            onAction: deleteCatalog,
            loading,
            destructive: true,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: () => setDeleteModalActive(false),
            },
          ]}
        >
          <Modal.Section>
            <TextContainer>
              <p>
                Are you sure you want to delete the catalog "{catalogToDelete?.name}"?
              </p>
              <p>
                This will remove the catalog from WhatsApp and delete all associated
                product mappings. This action cannot be undone.
              </p>
            </TextContainer>
          </Modal.Section>
        </Modal>

        {toastMarkup}
      </Page>
    </Frame>
  );
};
